import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { convertFromAIGenerateToScript } from '../../utils/convertFromAiGenerateToScript';
import { DataFlowContext } from './DataFlowContext';
import { useSearchParams } from 'react-router-dom';
import { getModelConfigFromDb, saveModelConfigToDb } from '../../request/Automation';

const ChatContext = createContext();

const ChatContextProvider = ({ addScript, getPosition, handleShowVariables, children}) => {
  const [messages, setMessages] = useState([]);
  const [loading, setIsLoading] = useState(false);
  const { createNewVariables } = useContext(DataFlowContext);
  const chatId = useRef();
  const promtRef = useRef(null);
  const [searchParams] = useSearchParams();
  let MODEL_CONFIG_STORAGE_KEY = 'hidemium_model_config';
  const user_uuid = searchParams.get('user_uuid');

  useEffect(() => {
    if (!user_uuid) {
      try {
        const savedConfig = localStorage.getItem(MODEL_CONFIG_STORAGE_KEY);
        setModelConfig(savedConfig ? JSON.parse(savedConfig) : {
          provider: '',
          model: null,
          apiKey: null
        });
      } catch (error) {
        console.error('Error loading model config from localStorage:', error);
        setModelConfig({
          provider: '',
          model: null,
          apiKey: null
        });
      }
    } else {
      const initModelConfig = async () => {
        let MODEL_CONFIG_KEY = 'hidemium_model_config';
        const oldKeyData = localStorage.getItem(MODEL_CONFIG_KEY);
        localStorage.removeItem(MODEL_CONFIG_KEY);
        if (oldKeyData) {
          const MODEL_CONFIG_KEY = 'hidemium_model_config_' + user_uuid;
          await saveModelConfigToDb(MODEL_CONFIG_KEY, JSON.parse(oldKeyData));
          localStorage.removeItem(MODEL_CONFIG_KEY);
        }
        MODEL_CONFIG_KEY = 'hidemium_model_config_' + user_uuid;
        const config = await getModelConfigFromDb(MODEL_CONFIG_KEY);
        if (config?.content) {
          setModelConfig(JSON.parse(config?.content?.settings));
        }
      };
      initModelConfig();
    }
  }, [])

  const [modelConfig, setModelConfig] = useState({
    provider: '',
    model: null,
    apiKey: null
  });

  const isInScriptWindow = searchParams.get('window') === "script";
  const sendMessage = (message) => {
    setIsLoading(true);
    setMessages(messages.concat([{ type: 'user', message }]));
    const sentData = {type: 'gen_script', message}
    if (!chatId.current) {
      chatId.current = Number(new Date())
      sentData.config = modelConfig
    }
    if (!isInScriptWindow) {
      window.top.postMessage(sentData, "*");
    } else {
      window.electron?.ipcRenderer.send('send-to-main-window', { action: 'gen_script', data: sentData});
    }
  }

  const updateModelConfig = async (config) => {
    chatId.current = null;
    setModelConfig(config);
    try {
      if (user_uuid) {
        const MODEL_CONFIG_KEY = 'hidemium_model_config_' + user_uuid;
        await saveModelConfigToDb(MODEL_CONFIG_KEY, config);
      } else {
        localStorage.setItem(MODEL_CONFIG_STORAGE_KEY, JSON.stringify(config));
      }
    } catch (error) {
      console.error('Error saving model config to localStorage:', error);
    }
  };

  const resetChat = () => {
    setMessages([]);
    setIsLoading(false);
    chatId.current = null;
    setTimeout(() => {
      promtRef.current?.focus();
    }, 50)
  }

  const value = {
    loading,
    messages,
    sendMessage,
    handleShowVariables,
    modelConfig,
    updateModelConfig,
    resetChat,
    promtRef,
  }

  useEffect(() => {
    const listener = (event) => {
      if (event.data.type === 'gen_script') {
        setIsLoading(false);
        const content = event.data.data?.content;
        if (content) {
          console.log(content);
          console.log(modelConfig);
          if (content.includes("Sorry, I encountered an error processing your request.")) {
            const modelInfo = modelConfig ? (modelConfig.provider === 'hidemium'
              ? 'Hidemium Free'
              : `${modelConfig.provider} (${modelConfig.model})`) : 'Chưa xác định';
            setMessages((msgs) => msgs.concat([{
              type: 'model',
              message: `Sorry, I encountered an error processing your request. Please change your config model. (Current model: ${modelInfo})`
            }]));
          } else {
            setMessages((msgs) => msgs.concat([{
              type: 'model',
              message: content,
            }]));
          }
        }
        const script = event.data?.data.script
        if (script) {
          const position = getPosition()
          const dataEncode = convertFromAIGenerateToScript({ content: script, position })
          console.log(dataEncode);
          addScript(dataEncode)
          if (dataEncode.variables.size) {
            createNewVariables(dataEncode.variables);
          }
        }
      }
    }
    window.addEventListener("message", listener);
    window.electron?.ipcRenderer.on('from-main-window', (_, data) => {
      if (data?.type === 'gen_script') {
        listener({ data });
      }
    })
    return () => {
      window.removeEventListener("message", listener);
      window.electron?.ipcRenderer.removeAllListeners('from-main-window');
    }
  }, [modelConfig])

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  )
}

export { ChatContext, ChatContextProvider };
